import { Component, EventEmitter, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { SkillService } from 'src/app/common/services/skill/skill.service';
import { StatusEnum } from 'src/app/common/utils/status-enum.models';
import { PagedRequest } from 'src/app/paged-request.model';
import { ButtonColorEnum } from 'src/app/shared/buttons/enums/button-color.enum';
import { InputTypeEnum } from 'src/app/shared/input/enums/input-type.enum';
import {
  GetClientRequest,
  GetClientsResponse,
  GetSkillRequest,
  GetSkillResponse,
} from '../../client/models/client.model';
import { ClientService } from '../../client/service/client.service';
import { ContactReasonService } from '../../contact-reason/service/contact-reason.service';
import {
  GetContactReasonFilter,
  Macro,
} from '../../contact-reason/service/models/contact-reason.service.model';
import {
  GetUserRequest,
  GetUserResponse,
} from '../../user/service/models/user.service.model';
import { UserService } from '../../user/service/user.service';
import { DashboardFilterRequest } from '../service/models/dashboard.service.model';

@Component({
  selector: 'app-dashboard-filter',
  templateUrl: './dashboard-filter.component.html',
  styleUrls: ['./dashboard-filter.component.scss'],
})
export class DashboardFilterComponent {
  @Output() filterEmmiter: EventEmitter<DashboardFilterRequest> =
    new EventEmitter<DashboardFilterRequest>();

  isLoading: boolean = false;
  error: boolean = false;

  clients: string[] = [];
  originalClients: GetClientsResponse[] = [];
  selectedClients: string[] = [];

  skills: string[] = [];
  selectedSkills: string[] = [];
  originalSkills: GetSkillResponse[] = [];

  cells: string[] = [];
  selectedCells: string[] = [];

  subCells: string[] = [];
  selectedSubCells: string[] = [];

  supervisors: string[] = [];
  selectedSupervisors: string[] = [];
  originalSupervisors: GetUserResponse[] = [];

  attendants: string[] = [];
  selectedAttendants: string[] = [];
  originalAttendants: GetUserResponse[] = [];

  shifts: string[] = ['Manhã', 'Tarde', 'Noite', 'Madrugada'];
  selectedShifts: string[] = [];
  loadingShifts = false;

  types: string[] = ['Áudio', 'Chat'];
  selectedTypes: string[] = [];

  macroContactReasonsOptions: string[] = [];
  selectedMacroContactReason: string[] = [];
  originalContactReasons: Macro[] = [];

  microContactReasonsOptions: string[] = [];
  selectedMicroContactReason: string[] = [];

  loadingClients: boolean = false;
  loadingSearchClients: boolean = false;
  loadingMoreClients: boolean = false;
  totalItemsClients: number = 0;
  clientFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    sortAscending: true,
    sortBy: 'Name',
  } as GetClientRequest;

  loadingSupervisors: boolean = false;
  loadingSearchSupervisors: boolean = false;
  loadingMoreSupervisors: boolean = false;
  totalItemsSupervisors: number = 0;
  supervisorsFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    profiles: ['SUPERVISOR'],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  loadingAttendants: boolean = false;
  loadingSearchAttendants: boolean = false;
  loadingMoreAttendants: boolean = false;
  totalItemsAttendants: number = 0;
  attendantsFilter = {
    pageNumber: 1,
    pageSize: 10,
    status: [StatusEnum.ATIVO],
    profiles: ['ATENDENTE'],
    sortAscending: true,
    sortBy: 'Name',
  } as GetUserRequest;

  loadingSkills: boolean = false;
  loadingSearchSkills: boolean = false;
  loadingMoreSkills: boolean = false;
  totalItemsSkills: number = 0;
  skillsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'Code',
  } as GetSkillRequest;

  loadingCells: boolean = false;
  loadingSearchCells: boolean = false;
  loadingMoreCells: boolean = false;
  totalItemsCells: number = 0;
  cellsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'Cell',
  } as PagedRequest;

  loadingSubcells: boolean = false;
  loadingSearchSubCells: boolean = false;
  loadingMoreSubCells: boolean = false;
  totalItemsSubCells: number = 0;
  subCellsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'SubCell',
  } as PagedRequest;

  loadingContactReasons: boolean = false;
  loadingSearchContactReasons: boolean = false;
  loadingMoreContactReasons: boolean = false;
  totalItemsContactReasons: number = 0;
  contactReasonsFilter = {
    pageNumber: 1,
    pageSize: 10,
    sortAscending: true,
    sortBy: 'Name',
  } as GetContactReasonFilter;

  form = this.formBuilder.group({
    initialDate: '',
    finalDate: '',
    supervisors: [[]],
    attendants: [[]],
    clients: [[]],
    skills: [[]],
    cells: [[]],
    subCells: [[]],
    shifts: [[]],
    types: [['']],
    macroContactReasons: [] as string[] | null,
    microContactReasons: [] as string[] | null,
  });

  ButtonColorEnum = ButtonColorEnum;
  InputTypeEnum = InputTypeEnum;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly userService: UserService,
    private readonly clientService: ClientService,
    private readonly skillService: SkillService,
    private readonly contactReasonService: ContactReasonService,
    private readonly toastrService: ToastrService
  ) {}

  ngOnInit() {
    const currentDate = new Date();
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );

    this.form.patchValue({
      initialDate: this.formatDate(firstDayOfMonth),
      finalDate: this.formatDate(currentDate),
    });

    this.form.controls.macroContactReasons.valueChanges.subscribe(
      (value: string[] | null) => {
        if (value && value.length > 0 && value[0] !== '') {
          const macroNames = value.map((i) => i.trim());

          const macro = this.originalContactReasons.filter((macro) =>
            macroNames.includes(macro.name)
          );

          const filteredMicro = macro
            .map((macro) => macro.micros.map((micro) => micro))
            .flat();

          this.microContactReasonsOptions = filteredMicro;

          this.selectedMicroContactReason = [];
          this.form.controls.microContactReasons.reset();
        } else {
          this.microContactReasonsOptions = this.originalContactReasons.flatMap(
            (item) => item.micros.map((subItem) => subItem.trim())
          );
          this.selectedMicroContactReason = [];
          this.form.controls.microContactReasons.reset();
        }

        if (
          !this.form.controls.macroContactReasons.value ||
          this.form.controls.macroContactReasons.value.length === 0 ||
          (this.form.controls.macroContactReasons.value.length === 1 &&
            this.form.controls.macroContactReasons.value[0] === '')
        ) {
          this.microContactReasonsOptions = this.originalContactReasons.flatMap(
            (item) => item.micros.map((subItem) => subItem.trim())
          );
          this.selectedMicroContactReason = [];
          this.form.controls.microContactReasons.reset();
        }
      }
    );

    this.getClients(null);
    this.getSkills();
    this.getCells();
    this.getSubCells();
    this.getSupervisors(null);
    this.getAttendants(null);
    this.getContactReasons();

    this.filter();
  }

  getClients(name: string | null) {
    this.clientFilter.pageNumber = 1;

    if (name) {
      this.clientFilter.name = name;
      this.loadingSearchClients = true;
    } else {
      this.loadingClients = true;
      this.clientFilter.name = null;
    }

    this.clientService.get(this.clientFilter).subscribe({
      next: (data) => {
        this.originalClients = data.items;
        this.clients = data.items.map((i) => i.name + ' | ' + i.cnpj);
        this.totalItemsClients = data.totalItems;
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os clientes.');
        this.loadingClients = false;
        this.loadingSearchClients = false;
      },
    });
  }

  getMoreClients() {
    if (this.totalItemsClients != this.clients.length) {
      this.loadingMoreClients = true;
      if (this.clientFilter.pageNumber) this.clientFilter.pageNumber += 1;

      this.clientService.get(this.clientFilter).subscribe({
        next: (data) => {
          this.originalClients = this.originalClients.concat(data.items);
          this.totalItemsClients = data.totalItems;
          this.clients = this.clients.concat(
            data.items.map((i) => i.name + ' | ' + i.cnpj)
          );
          this.loadingMoreClients = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os clientes.');
          this.loadingMoreClients = false;
        },
      });
    }
  }

  getSkills() {
    this.skillsFilter.pageNumber = 1;
    this.loadingSkills = true;

    this.skillService.get(this.skillsFilter).subscribe({
      next: (data) => {
        this.originalSkills = data.items;

        this.skills = data.items.map(
          (i) => i.code + ': ' + i.cell + ' — ' + i.subCell
        );
        this.totalItemsSkills = data.totalItems;
        this.loadingSkills = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as skills.');
        this.loadingSkills = false;
      },
    });
  }

  getMoreSkills() {
    if (this.totalItemsSkills != this.skills.length) {
      this.loadingMoreSkills = true;
      if (this.skillsFilter.pageNumber) this.skillsFilter.pageNumber += 1;

      this.skillService.get(this.skillsFilter).subscribe({
        next: (data) => {
          this.originalSkills = this.originalSkills.concat(data.items);
          this.totalItemsSkills = data.totalItems;
          this.skills = this.skills.concat(
            data.items.map((i) => i.code + ': ' + i.cell + ' — ' + i.subCell)
          );
          this.loadingMoreSkills = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as skills.');
          this.loadingMoreSkills = false;
        },
      });
    }
  }

  getCells() {
    this.cellsFilter.pageNumber = 1;
    this.loadingCells = true;

    this.skillService.getCells(this.cellsFilter).subscribe({
      next: (data) => {
        this.cells = data.items;
        this.totalItemsCells = data.totalItems;
        this.loadingCells = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as células.');
        this.loadingCells = false;
      },
    });
  }

  getMoreCells() {
    if (this.totalItemsCells != this.cells.length) {
      this.loadingMoreCells = true;
      if (this.cellsFilter.pageNumber) this.cellsFilter.pageNumber += 1;

      this.skillService.getCells(this.cellsFilter).subscribe({
        next: (data) => {
          this.cells = this.cells.concat(data.items);
          this.totalItemsCells = data.totalItems;
          this.loadingMoreCells = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as células.');
          this.loadingMoreCells = false;
        },
      });
    }
  }

  getSubCells() {
    this.subCellsFilter.pageNumber = 1;
    this.loadingSubcells = true;

    this.skillService.getSubCells(this.subCellsFilter).subscribe({
      next: (data) => {
        this.subCells = data.items;
        this.totalItemsSubCells = data.totalItems;
        this.loadingSubcells = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar as sub-células.');
        this.loadingSubcells = false;
      },
    });
  }

  getMoreSubCells() {
    if (this.totalItemsSubCells != this.subCells.length) {
      this.loadingMoreSubCells = true;
      if (this.subCellsFilter.pageNumber) this.subCellsFilter.pageNumber += 1;

      this.skillService.getSubCells(this.subCellsFilter).subscribe({
        next: (data) => {
          this.subCells = this.subCells.concat(data.items);
          this.totalItemsSubCells = data.totalItems;
          this.loadingMoreSubCells = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar as sub-células.');
          this.loadingMoreSubCells = false;
        },
      });
    }
  }

  getSupervisors(name: string | null) {
    this.supervisorsFilter.pageNumber = 1;

    if (name) {
      this.supervisorsFilter.name = name;
      this.loadingSearchSupervisors = true;
    } else {
      this.loadingSupervisors = true;
      this.supervisorsFilter.name = null;
    }

    this.userService.get(this.supervisorsFilter).subscribe({
      next: (data) => {
        this.originalSupervisors = data.items;
        this.supervisors = data.items.map(
          (supervisor) => supervisor.name + ' | ' + supervisor.functional
        );
        this.totalItemsSupervisors = data.totalItems;
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os supervisores.');
        this.loadingSupervisors = false;
        this.loadingSearchSupervisors = false;
      },
    });
  }

  getMoreSupervisors() {
    if (this.totalItemsSupervisors != this.supervisors.length) {
      this.loadingMoreSupervisors = true;
      if (this.supervisorsFilter.pageNumber)
        this.supervisorsFilter.pageNumber += 1;

      this.userService.get(this.supervisorsFilter).subscribe({
        next: (data) => {
          this.originalSupervisors = this.originalSupervisors.concat(
            data.items
          );
          this.supervisors = this.supervisors.concat(
            data.items.map(
              (supervisor) => supervisor.name + ' | ' + supervisor.functional
            )
          );
          this.totalItemsSupervisors = data.totalItems;
          this.loadingMoreSupervisors = false;
        },
        error: () => {
          this.toastrService.error(
            'Não foi possível carregar os supervisores.'
          );
          this.loadingMoreSupervisors = false;
        },
      });
    }
  }

  getAttendants(name: string | null) {
    this.attendantsFilter.pageNumber = 1;

    if (name) {
      this.attendantsFilter.name = name;
      this.loadingSearchAttendants = true;
    } else {
      this.loadingAttendants = true;
      this.attendantsFilter.name = null;
    }

    this.userService.get(this.attendantsFilter).subscribe({
      next: (data) => {
        this.originalAttendants = data.items;
        this.attendants = data.items.map(
          (attendant) => attendant.name + ' | ' + attendant.functional
        );
        this.totalItemsAttendants = data.totalItems;
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
      error: () => {
        this.toastrService.error('Não foi possível carregar os atendentes.');
        this.loadingAttendants = false;
        this.loadingSearchAttendants = false;
      },
    });
  }

  getMoreAttendants() {
    if (this.totalItemsAttendants != this.attendants.length) {
      this.loadingMoreAttendants = true;
      if (this.attendantsFilter.pageNumber)
        this.attendantsFilter.pageNumber += 1;

      this.userService.get(this.attendantsFilter).subscribe({
        next: (data) => {
          this.originalAttendants = this.originalAttendants.concat(data.items);
          this.attendants = this.attendants.concat(
            data.items.map(
              (attendant) => attendant.name + ' | ' + attendant.functional
            )
          );
          this.totalItemsAttendants = data.totalItems;
          this.loadingMoreAttendants = false;
        },
        error: () => {
          this.toastrService.error('Não foi possível carregar os atendentes.');
          this.loadingMoreAttendants = false;
        },
      });
    }
  }

  getContactReasons() {
    this.contactReasonsFilter.pageNumber = 1;
    this.loadingContactReasons = true;

    this.contactReasonService.getMacro(this.contactReasonsFilter).subscribe({
      next: (data) => {
        this.originalContactReasons = data.items;

        this.macroContactReasonsOptions = this.originalContactReasons.map(
          (item) => item.name.trim()
        );

        this.microContactReasonsOptions = this.originalContactReasons.flatMap(
          (item) =>
            item.micros.map(
              (subItem) => subItem.trim() + ' (' + item.name + ')'
            )
        );

        this.totalItemsContactReasons = data.totalItems;
        this.loadingContactReasons = false;
      },
      error: () => {
        this.toastrService.error(
          'Não foi possível carregar os motivos de contato.'
        );
        this.loadingContactReasons = false;
      },
    });
  }

  getMoreContactReasons() {
    if (
      this.totalItemsContactReasons != this.macroContactReasonsOptions.length
    ) {
      this.loadingMoreContactReasons = true;
      if (this.contactReasonsFilter.pageNumber)
        this.contactReasonsFilter.pageNumber += 1;

      this.contactReasonService.getMacro(this.contactReasonsFilter).subscribe({
        next: (data) => {
          this.originalContactReasons = this.originalContactReasons.concat(
            data.items
          );

          this.macroContactReasonsOptions =
            this.macroContactReasonsOptions.concat(
              data.items.map((item) => item.name.trim())
            );

          this.microContactReasonsOptions =
            this.microContactReasonsOptions.concat(
              data.items.flatMap((item) =>
                item.micros.map(
                  (subItem) => subItem.trim() + ' (' + item.name + ')'
                )
              )
            );

          this.totalItemsContactReasons = data.totalItems;
          this.loadingMoreContactReasons = false;
        },
        error: () => {
          this.toastrService.error(
            'Não foi possível carregar os motivos de contato.'
          );
          this.loadingMoreContactReasons = false;
        },
      });
    }
  }

  getFilteredShiftsValue(text: string[] | null): string[] | null {
    if (!text || text.length == 0 || (text.length == 1 && text[0] == ''))
      return null;
    return this.form.controls.shifts.value;
  }

  formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  clear() {
    this.form.reset();
    this.selectedClients = [];
    this.selectedAttendants = [];
    this.selectedSkills = [];
    this.selectedCells = [];
    this.selectedSubCells = [];
    this.selectedSupervisors = [];
    this.selectedShifts = [];
    this.selectedTypes = [];
    this.selectedMacroContactReason = [];
    this.selectedMicroContactReason = [];
  }

  getClientsIdFromText(clients: string[]): string[] {
    if (!clients || clients.length === 0) return [];
    let clientsId: string[] = [];
    clients.forEach((name) => {
      let clientName = name.split('|')[0].trim();
      let client = this.originalClients.find(
        (i) => i.name.trim() === clientName.trim()
      );
      if (client) clientsId.push(client.id);
    });

    return clientsId;
  }

  filter() {
    const clientIds = this.getClientsIdFromText(
      this.form.controls.clients.value ?? []
    );

    const selectedClients: string[] = this.form.controls.clients.value ?? [];

    if (selectedClients.length > 0) {
      if (
        clientIds === null ||
        clientIds.length === 0 ||
        clientIds.length !== selectedClients.length
      ) {
        this.isLoading = false;
        this.error = true;
        this.toastrService.error(
          'Não foi possível recuperar os clientes selecionados.'
        );
        return;
      }
    }

    const shifts = this.getFilteredShiftsValue(this.form.controls.shifts.value);

    let types: number[] | null;

    if (
      !this.form.controls.types?.value ||
      (this.form.controls.types.value.length == 1 &&
        this.form.controls.types.value[0].trim() == '')
    )
      types = null;
    else {
      types =
        this.form.controls.types.value?.map((i) => {
          return this.types.indexOf(i);
        }) ?? null;
    }

    const filters = {
      startDate: this.form.controls.initialDate.value ?? null,
      endDate: this.form.controls.finalDate.value ?? null,
      clients: clientIds,
      cells: this.form.controls.cells.value ?? null,
      supervisors:
        this.getSupervisorsIdFromText(
          this.form.controls.supervisors.value ?? []
        ) ?? null,
      attendants:
        this.getAttendantsIdFromText(
          this.form.controls.attendants.value ?? []
        ) ?? null,
      subCells: this.form.controls.subCells.value ?? null,
      shifts: shifts,
      types: types,
      skills: this.getSkillIdFromText(this.form.controls.skills.value ?? []),
      macroContactReasons: this.form.controls.macroContactReasons.value ?? null,
      microContactReasons: this.form.controls.microContactReasons.value ?? null,
      topQuantity: null,
    } as DashboardFilterRequest;

    this.filterEmmiter.emit(filters);
  }

  getAttendantsIdFromText(text: string[]): string[] {
    let attendantIds: string[] = [];

    text.forEach((attendantText) => {
      let attendantId = this.getAttendantIdFromText(attendantText);
      if (attendantId) attendantIds.push(attendantId);
    });

    return attendantIds;
  }

  getAttendantIdFromText(text: string): string | null {
    if (!text || text.trim() === '') return null;

    const [name, functionalString] = text.split('|').map((part) => part.trim());
    const attendant = this.originalAttendants.find(
      (attendant) =>
        attendant.functional.toString() === functionalString &&
        attendant.name.trim() === name.trim()
    );

    return attendant ? attendant.id : null;
  }

  getSupervisorsIdFromText(text: string[]): string[] | null {
    if (!text || text.length === 0) return null;

    const supervisorsId: string[] = [];

    text.forEach((s) => {
      const [name, functionalString] = s.split('|').map((part) => part.trim());
      const supervisor = this.originalSupervisors.find(
        (attendant) =>
          attendant.functional.toString() === functionalString &&
          attendant.name.trim() === name.trim()
      );

      if (supervisor) supervisorsId.push(supervisor.id);
    });

    return supervisorsId;
  }

  getSkillIdFromText(text: string[]): string[] | null {
    if (!text || text.length === 0) return null;

    var skills: string[] = [];

    for (let i = 0; i < text.length; i++) {
      if (!text[i] || text[i].trim() === '') return null;

      var [code, cellAndSubCell] = text[i].split(':').map((i) => i.trim());
      var [cell, subCell] = cellAndSubCell.split('—').map((i) => i.trim());

      var skillFiltered = this.originalSkills.find(
        (i) =>
          i.code.toString() === code &&
          i.cell.trim() === cell &&
          i.subCell.trim() === subCell
      );

      if (skillFiltered) skills.push(skillFiltered.id);
    }

    return skills;
  }

  isOpen: boolean = false;

  handleToggleAccordion() {
    this.isOpen = !this.isOpen;
  }
}
